import React, { useState } from "react";
import { MealPlanData, DayMealPlan, Meal, Recipe } from "types/mealPlanning";
import { formatIngredient } from "utils/exportUtils";

interface PrintableMealPlanProps {
  mealPlanData: MealPlanData;
  onClose?: () => void;
}

export const PrintableMealPlan: React.FC<PrintableMealPlanProps> = ({
  mealPlanData,
  onClose,
}) => {
  const [columnsPerRow, setColumnsPerRow] = useState(2);

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    // Build the HTML content for printing with grid layout
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Meal Plan - Print View</title>
          <style>
            @page {
              size: letter;
              margin: 0.5in;
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 0;
              font-size: 9px;
              line-height: 1.2;
            }
            
            .meal-plan-grid {
              display: grid;
              grid-template-columns: repeat(${columnsPerRow}, 1fr);
              gap: 10px;
              width: 100%;
            }
            
            .day-card {
              border: 1px solid #999;
              padding: 8px;
              break-inside: avoid;
              page-break-inside: avoid;
              -webkit-column-break-inside: avoid;
              display: inline-block;
              width: 100%;
              box-sizing: border-box;
              overflow: hidden;
            }
            
            .day-header {
              border-bottom: 1px solid #999;
              padding-bottom: 5px;
              margin-bottom: 6px;
            }
            
            .day-title {
              font-size: 12px;
              font-weight: bold;
              margin: 0 0 2px 0;
            }
            
            .day-macros {
              font-size: 8px;
              color: #666;
              margin: 0;
            }
            
            .meal-section {
              margin-bottom: 6px;
            }
            
            .meal-title {
              font-size: 9px;
              font-weight: bold;
              text-transform: uppercase;
              color: #333;
              margin-bottom: 2px;
            }
            
            .recipe {
              margin-bottom: 5px;
              padding-left: 5px;
            }
            
            .recipe-header {
              display: flex;
              justify-content: space-between;
              margin-bottom: 2px;
              align-items: flex-start;
            }
            
            .recipe-name {
              font-weight: 600;
              font-size: 9px;
              flex: 1;
              padding-right: 3px;
            }
            
            .recipe-calories {
              font-size: 8px;
              color: #666;
              white-space: nowrap;
            }
            
            .ingredients {
              font-size: 8px;
              color: #333;
              margin-bottom: 2px;
            }
            
            .ingredient-item {
              margin-left: 8px;
              line-height: 1.2;
            }
            
            .preparation {
              margin-top: 3px;
              margin-left: 5px;
              padding-left: 5px;
              border-left: 1.5px solid #666;
            }
            
            .preparation-label {
              font-size: 8px;
              font-weight: bold;
              color: #333;
              margin-bottom: 1px;
            }
            
            .preparation-text {
              font-size: 8px;
              color: #666;
              line-height: 1.2;
            }
            
            @media print {
              body {
                margin: 0;
              }
              
              .meal-plan-grid {
                display: flex;
                flex-wrap: wrap;
                gap: 15px;
              }
              
              .day-card {
                width: calc((100% - 15px) / ${columnsPerRow});
                margin-bottom: 15px;
                break-inside: avoid;
                page-break-inside: avoid;
                -webkit-column-break-inside: avoid;
                box-sizing: border-box;
              }
              
              /* Force new row after every ${columnsPerRow} cards */
              .day-card:nth-child(${columnsPerRow}n) {
                margin-right: 0;
              }
              
              /* Start new row on new page if needed */
              .day-card:nth-child(${columnsPerRow * 2 + 1}) {
                page-break-before: always;
              }
              
              /* Ensure cards don't overflow page height */
              @page {
                size: letter portrait;
                margin: 0.5in;
              }
              
              /* Reduce spacing even more for print */
              .meal-section:last-child {
                margin-bottom: 0;
              }
              
              .recipe:last-child {
                margin-bottom: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="meal-plan-grid">
            ${mealPlanData.mealPlanDays.map((day, dayIndex) => {
              const dayNutrition = getDayNutrition(day);
              return `
                <div class="day-card">
                  <div class="day-header">
                    <h2 class="day-title">Day ${dayIndex + 1}</h2>
                    <p class="day-macros">Daily Target: ${formatMacros(dayNutrition)}</p>
                  </div>
                  ${["breakfast", "lunch", "dinner", "snack"].map((mealType) => {
                    const meal = day[mealType as keyof DayMealPlan] as Meal;
                    if (!meal || meal.recipes.length === 0) return '';
                    
                    return `
                      <div class="meal-section">
                        <h3 class="meal-title">${mealType}</h3>
                        ${meal.recipes.map((recipe) => {
                          const recipeNutrition = getRecipeNutrition(recipe);
                          return `
                            <div class="recipe">
                              <div class="recipe-header">
                                <span class="recipe-name">${recipe.name}</span>
                                <span class="recipe-calories">${Math.round(recipeNutrition.calories)}cal</span>
                              </div>
                              <div class="ingredients">
                                ${recipe.ingredients.map((ingredient) => 
                                  `<div class="ingredient-item">• ${formatIngredient(ingredient)}</div>`
                                ).join('')}
                              </div>
                              ${recipe.preparation ? `
                                <div class="preparation">
                                  <div class="preparation-label">Preparation:</div>
                                  <div class="preparation-text">${recipe.preparation}</div>
                                </div>
                              ` : ''}
                            </div>
                          `;
                        }).join('')}
                      </div>
                    `;
                  }).join('')}
                </div>
              `;
            }).join('')}
          </div>
        </body>
      </html>
    `;

    // Write the content to the new window and print
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  const getMealNutrition = (meal: Meal) => {
    if (meal.totalNutrition) {
      return meal.totalNutrition;
    }
    // Calculate from recipes
    const totals = { calories: 0, carbs: 0, fat: 0, protein: 0 };
    meal.recipes.forEach((recipe) => {
      const nutrition = getRecipeNutrition(recipe);
      totals.calories += nutrition.calories;
      totals.carbs += nutrition.carbs;
      totals.fat += nutrition.fat;
      totals.protein += nutrition.protein;
    });
    return totals;
  };

  const getRecipeNutrition = (recipe: Recipe) => {
    if (recipe.macros) {
      return recipe.macros;
    }
    if (recipe.totalNutrition) {
      return recipe.totalNutrition;
    }
    return { calories: 0, carbs: 0, fat: 0, protein: 0 };
  };

  const getDayNutrition = (day: DayMealPlan) => {
    if (day.macros) {
      return day.macros;
    }
    if (day.totalNutrition) {
      return day.totalNutrition;
    }
    // Calculate from meals
    const totals = { calories: 0, carbs: 0, fat: 0, protein: 0 };
    ["breakfast", "lunch", "dinner", "snack"].forEach((mealType) => {
      if (day[mealType as keyof typeof day]) {
        const meal = day[mealType as keyof typeof day] as Meal;
        const nutrition = getMealNutrition(meal);
        totals.calories += nutrition.calories || 0;
        totals.protein += nutrition.protein || 0;
        totals.carbs += nutrition.carbs || 0;
        totals.fat += nutrition.fat || 0;
      }
    });
    return totals;
  };

  const formatMacros = (nutrition: { calories: number; carbs: number; fat: number; protein: number }) => {
    return `${Math.round(nutrition.calories)}cal | ${Math.round(nutrition.carbs)}g C | ${Math.round(nutrition.fat)}g F | ${Math.round(nutrition.protein)}g P`;
  };

  const gridClassName = `grid gap-4 ${
    columnsPerRow === 1 ? "grid-cols-1" : 
    columnsPerRow === 2 ? "grid-cols-2" : 
    columnsPerRow === 3 ? "grid-cols-3" : 
    columnsPerRow === 4 ? "grid-cols-4" : 
    "grid-cols-3"
  }`;

  return (
    <div className="printable-meal-plan">
      {/* Controls */}
      <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg shadow">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <span className="font-medium">Columns per row:</span>
            <select
              value={columnsPerRow}
              onChange={(e) => setColumnsPerRow(Number(e.target.value))}
              className="border rounded px-2 py-1"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </label>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handlePrint}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            🖨️ Print Meal Plan
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Printable content */}
      <div className={gridClassName}>
        {mealPlanData.mealPlanDays.map((day, dayIndex) => {
          const dayNutrition = getDayNutrition(day);
          
          return (
            <div
              key={dayIndex}
              className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 print:break-inside-avoid"
            >
              {/* Day Header */}
              <div className="mb-3 pb-2 border-b border-gray-300">
                <h2 className="text-lg font-bold">Day {dayIndex + 1}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Daily Target: {formatMacros(dayNutrition)}
                </p>
              </div>

              {/* Meals */}
              {["breakfast", "lunch", "dinner", "snack"].map((mealType) => {
                const meal = day[mealType as keyof DayMealPlan] as Meal;
                if (!meal || meal.recipes.length === 0) return null;

                return (
                  <div key={mealType} className="mb-3">
                    <h3 className="font-semibold text-sm uppercase text-gray-700 mb-1">
                      {mealType}
                    </h3>
                    
                    {meal.recipes.map((recipe, recipeIndex) => {
                      const recipeNutrition = getRecipeNutrition(recipe);
                      
                      return (
                        <div key={recipeIndex} className="mb-2 pl-2">
                          {/* Recipe name with inline macros */}
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-medium text-sm">{recipe.name}</h4>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {Math.round(recipeNutrition.calories)}cal
                            </span>
                          </div>
                          
                          {/* Ingredients */}
                          <div className="text-xs text-gray-700 mb-1">
                            {recipe.ingredients.map((ingredient, idx) => (
                              <div key={idx} className="ml-2">
                                • {formatIngredient(ingredient)}
                              </div>
                            ))}
                          </div>
                          
                          {/* Preparation */}
                          {recipe.preparation && (
                            <div className="mt-2 ml-2 pl-2 border-l-2 border-gray-400">
                              <div className="text-xs font-semibold text-gray-700 mb-1">
                                Preparation:
                              </div>
                              <div className="text-xs text-gray-600">
                                {recipe.preparation}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
        </div>
  );
}; 